/**
 * Vocabulário e política de acesso do produto. Fonte única compartilhada por
 * backend e interface: antes deste módulo, papéis, ações e a matriz existiam
 * escritos à mão em `plugins/spassu-code-backend/src/types-rbac.ts`,
 * `service/rbac-permissions.ts` e `plugins/spassu-code/src/api/types.ts`, sem
 * nenhuma verificação de equivalência entre as cópias.
 *
 * Papéis e ações são tuplas, não uniões escritas à mão, porque união de tipo é
 * apagada na compilação: sem a lista em runtime, um teste de exaustividade da
 * matriz precisaria de uma segunda lista literal — exatamente a duplicação que
 * este módulo elimina.
 */

export const RBAC_ROLES = [
  'admin',
  'gestor',
  'analista',
  'desenvolvedor',
  'revisor',
  'aprovador',
  'observador',
] as const;

export type Role = (typeof RBAC_ROLES)[number];

export const RBAC_ACTIONS = [
  'member.add',
  'member.remove',
  'member.list',
  'activity.create',
  'activity.read.own',
  'activity.read.project',
  'activity.delete.own',
  'activity.bypass_owner_lock',
  'phase.interact',
  'phase.approve',
  'phase.reject',
  'artifact.view',
  'artifact.approve',
  'artifact.reject',
  'workitem.read',
  'workitem.write',
  'workitem.assign',
  'skill.manage',
  'skill.review',
  'skill.bind.project',
  'audit.view',
  'audit.export',
] as const;

export type RbacAction = (typeof RBAC_ACTIONS)[number];

/**
 * Matriz de permissões: ação → papéis permitidos. Fonte única de verdade da
 * autorização; a interface a consome apenas para condicionar o que oferece, e
 * o servidor continua sendo a fronteira que decide.
 *
 * O `Record<RbacAction, ...>` garante exaustividade em tempo de compilação:
 * ação nova sem linha aqui quebra o build.
 */
export const PERMISSION_MATRIX: Record<RbacAction, readonly Role[]> = {
  // Gestao de projeto
  'member.add': ['admin', 'gestor'],
  'member.remove': ['admin', 'gestor'],
  'member.list': ['admin', 'gestor', 'observador'],

  // Activities
  'activity.create': ['admin', 'gestor', 'analista', 'desenvolvedor'],
  'activity.read.own': [
    'admin',
    'gestor',
    'analista',
    'desenvolvedor',
    'revisor',
    'aprovador',
    'observador',
  ],
  'activity.read.project': [
    'admin',
    'gestor',
    'analista',
    'desenvolvedor',
    'revisor',
    'aprovador',
    'observador',
  ],
  'activity.delete.own': ['admin', 'gestor', 'analista', 'desenvolvedor'],
  // Quem pode acessar/editar atividades de terceiros independentemente do status.
  // Desenvolvedor NÃO tem: só acessa a própria ou atividades já concluídas.
  'activity.bypass_owner_lock': [
    'admin',
    'gestor',
    'analista',
    'revisor',
    'aprovador',
    'observador',
  ],

  // Pipeline / Fases
  'phase.interact': ['admin', 'gestor', 'analista', 'desenvolvedor'],
  'phase.approve': ['admin', 'gestor', 'aprovador'],
  'phase.reject': ['admin', 'gestor', 'revisor', 'aprovador'],

  // Artefatos
  'artifact.view': [
    'admin',
    'gestor',
    'analista',
    'desenvolvedor',
    'revisor',
    'aprovador',
    'observador',
  ],
  'artifact.approve': ['admin', 'revisor', 'aprovador'],
  'artifact.reject': ['admin', 'revisor', 'aprovador'],

  // Azure Boards — Work Items
  'workitem.read': ['admin', 'gestor', 'analista', 'desenvolvedor'],
  'workitem.write': ['admin', 'gestor', 'analista'], // v2 — nao enforced na v1 (somente leitura)
  'workitem.assign': ['admin', 'gestor', 'analista'], // v2 — nao enforced na v1 (somente leitura)

  // Skills
  'skill.manage': ['admin'],
  'skill.review': ['admin', 'gestor', 'revisor'],
  'skill.bind.project': ['admin', 'gestor'],

  // Auditoria
  'audit.view': ['admin', 'gestor', 'observador'],
  'audit.export': ['admin', 'gestor'],
};

/**
 * Teto de comprimento de um `ProviderId`. A auditoria de negativa de criação
 * compõe um identificador `<providerId>:<digest>` que precisa caber em uma
 * coluna de 100 caracteres; provider novo e comprido estouraria a conta em
 * silêncio. O contrato é o dono da verdade, então a invariante mora aqui e é
 * verificada no pacote do contrato.
 */
export const RBAC_PROVIDER_ID_MAX_LENGTH = 24;
